import {
  Module,
  Target,
  Question,
  VALID_ACTIVITIES,
  VALID_QUESTION_TYPES,
} from "./courseBuilderInterfaces";
import type { cleanCourse } from "./courseCleaners";

type CleanedCourse = ReturnType<typeof cleanCourse>[number];

export const validateCourse = (courses: CleanedCourse[]) => {
  let errors: Error[] = [];

  courses.forEach((course: any, index) => {
    // course_id is optional, only validate format if provided.
    if (course.course_id !== undefined && !Number.isInteger(course.course_id)) {
      errors.push(
        new Error(
          `Course validation failed at index ${index + 2} (course_id: ${
            course.course_id
          }): "course_id" must be an integer (received: ${course.course_id}).`
        )
      );
    }
  });

  return errors;
};

export const validateModules = (modules: Module[]) => {
  let errors: Error[] = [];

  modules.forEach((module, index) => {
    if (!module.module_id)
      errors.push(
        new Error(
          `Modules validation failed at index ${index + 2}: Missing required module_id`
        )
      );

    if (!module.name)
      errors.push(
        new Error(
          `Module validation failed at index ${index + 2} (module_id: ${
            module.module_id ?? "N/A"
          }): Missing required field "name".`
        )
      );

    if (module.order === undefined || module.order === null) {
      errors.push(
        new Error(
          `Module validation failed at index ${index + 2} (module_id: ${
            module.module_id ?? "N/A"
          }): Missing required field "order".`
        )
      );
    } else if (!Number.isInteger(module.order)) {
      errors.push(
        new Error(
          `Module validation failed at index ${index + 2} (module_id: ${
            module.module_id ?? "N/A"
          }): Invalid "order" — expected an integer but received "${module.order}".`
        )
      );
    }
  });

  return errors;
};

export const validateTargets = (targets: Target[], modules: Module[]) => {
  let errors: Error[] = [];

  const moduleIdSet = new Set(modules.map((m) => m.module_id));

  targets.forEach((target, index) => {
    if (!target.target_id)
      errors.push(
        new Error(
          `Targets/Submodules validation failed at index ${
            index + 2
          }: Missing required target_id`
        )
      );

    if (!target.name)
      errors.push(
        new Error(
          `Targets/Submodules validation failed at index ${index + 2} (target_id: ${
            target.target_id ?? "N/A"
          }): Missing required field "name".`
        )
      );

    if (!target.module_id) {
      errors.push(
        new Error(
          `Targets/Submodules validation failed at index ${index + 2} (target_id: ${
            target.target_id ?? "N/A"
          }): Missing required field "module_id".`
        )
      );
    } else if (!moduleIdSet.has(target.module_id)) {
      errors.push(
        new Error(
          `Targets/Submodules validation failed at index ${index + 2} (target_id: ${
            target.target_id ?? "N/A"
          }): Invalid module_id "${target.module_id}" — does not exist in modules.`
        )
      );
    }

    if (target.order === undefined) {
      errors.push(
        new Error(
          `Targets/Submodules validation failed at index ${index + 2} (target_id: ${
            target.target_id ?? "N/A"
          }): Missing required "order"`
        )
      );
    } else if (!Number.isInteger(target.order)) {
      errors.push(
        new Error(
          `Targets validation failed at index ${index + 2} (target_id: ${
            target.target_id ?? "N/A"
          }): "order" must be an integer (received: ${target.order})`
        )
      );
    }
  });

  return errors;
};

export const validateQuestions = (questions: Question[], targets: Target[]) => {
  let errors: Error[] = [];

  const targetIdSet = new Set(targets.map((t) => t.target_id));

  questions.forEach((question, index) => {
    if (!question.resource_link_title)
      errors.push(
        new Error(
          `Questions/Learning Map validation failed at index ${index + 2} (target_id: ${
            question.target_id ?? "N/A"
          }): Missing required field "resource link title".`
        )
      );

    if (!question.activity_type) {
      errors.push(
        new Error(
          `Questions/Learning Map validation failed at index ${index + 2} (target_id: ${
            question.target_id ?? "N/A"
          }): Missing required field "activity_type".`
        )
      );
    } else if (!VALID_ACTIVITIES.includes(question.activity_type as any)) {
      errors.push(
        new Error(
          `Questions/Learning Map validation failed at index ${index + 2} (target_id: ${
            question.target_id ?? "N/A"
          }): Invalid activity_type "${
            question.activity_type
          }". Allowed values are: ${VALID_ACTIVITIES.join(", ")}.`
        )
      );
    }

    if (!question.target_id) {
      errors.push(
        new Error(
          `Questions/Learning Map validation failed at index ${
            index + 2
          }: Missing required field "target id".`
        )
      );
    } else if (!targetIdSet.has(question.target_id)) {
      errors.push(
        new Error(
          `Questions/Learning Map validation failed at index${index + 2} (target_id: ${
            question.target_id ?? "N/A"
          }): Invalid target_id "${question.target_id}" — does not exist in targets.`
        )
      );
    }

    if (!question.type) {
      errors.push(
        new Error(
          `Questions/Learning Map validation failed at index ${index + 2} (problem_id: ${
            question.problem_id ?? "N/A"
          }): Missing required field "type".`
        )
      );
    } else if (!VALID_QUESTION_TYPES.includes(question.type as any)) {
      errors.push(
        new Error(
          `Questions/Learning Map validation failed at index ${index + 2} (problem_id: ${
            question.problem_id ?? "N/A"
          }): Invalid type "${
            question.type
          }". Allowed values are: ${VALID_QUESTION_TYPES.join(", ")}.`
        )
      );
    }

    if (question.order === undefined) {
      errors.push(
        new Error(
          `Questions/Learning Map validation failed at index ${index + 2} (target_id: ${
            question.target_id ?? "N/A"
          }): Missing required "order"`
        )
      );
    } else if (!Number.isInteger(question.order)) {
      errors.push(
        new Error(
          `Questions/Learning Map validation failed at index ${index + 2} (target_id: ${
            question.target_id ?? "N/A"
          }): "order" must be an integer (received: ${question.order})`
        )
      );
    }

    if (!question.problem_id)
      errors.push(
        new Error(
          `Questions/Learning Map validation failed at index ${index + 2} (target_id: ${
            question.target_id ?? "N/A"
          }): Missing required "problem_id"`
        )
      );
  });

  return errors;
};
